import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../../views/components/common/SkuCopyRow";
import {
  InventoryPackItem,
  InventoryPackSummary,
} from "../../../../../../models/inventory/InventoryAdmin";

interface PackItemsPopupProps {
  open: boolean;
  pack: InventoryPackSummary | null;
  items: InventoryPackItem[];
  isLoading?: boolean;
  onClose: () => void;
  onProductClick: (productId: number) => void;
}

export default function PackItemsPopup({
  open,
  pack,
  items,
  isLoading = false,
  onClose,
  onProductClick,
}: PackItemsPopupProps) {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{ zIndex: 2000 }}
    >
      <Box
        sx={{
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            <CustomTypography variant="heading4" fontWeight={700}>
              Pack Items
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              {pack?.label || "Pack"} · #{pack?.packId}
            </CustomTypography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  BOM ITEMS ({items.length || pack?.productsCount || 0})
                </CustomTypography>

                {isLoading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={4}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : items.length === 0 ? (
                  <CustomTypography
                    variant="body3"
                    color="text.secondary"
                    sx={{ mt: 1.5 }}
                  >
                    No BOM items found for this pack.
                  </CustomTypography>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={1.5}
                    sx={{ mt: 1.5, maxHeight: 360, overflowY: "auto" }}
                  >
                    {items.map((item, itemIndex) => (
                      <Box
                        key={`${item.clientSku || item.productId}-${itemIndex}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          flexWrap: "wrap",
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: theme.palette.customColors.white,
                          border: `1px solid ${theme.palette.customColors.greyBorder}`,
                        }}
                      >
                        <Box
                          minWidth={0}
                          flex={1}
                          sx={{
                            cursor:
                              item.productId != null ? "pointer" : "default",
                          }}
                          onClick={() => {
                            if (item.productId != null) {
                              onProductClick(item.productId);
                            }
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            {item.productName || "—"}
                          </CustomTypography>
                          <CustomTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            Product ID: {item.productId ?? "—"}
                          </CustomTypography>
                          {item.clientSku?.trim() ? (
                            <Box sx={{ mt: 0.25 }}>
                              <SkuCopyRow
                                sku={item.clientSku}
                                fontWeight={600}
                              />
                            </Box>
                          ) : (
                            <CustomTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              SKU unavailable
                            </CustomTypography>
                          )}
                        </Box>
                        <CustomTypography variant="body2" fontWeight={600}>
                          Qty: {item.qty}
                        </CustomTypography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid2>

            <Grid2
              size={12}
              display="flex"
              justifyContent="flex-end"
              sx={{ pt: 1 }}
            >
              <AppButton label="Close" onClick={onClose} variant="grayscale" />
            </Grid2>
          </Grid2>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
